import type { DocsSection } from "@/presentation/data/docs/docs-content.types";

export const gettingStartedSection: DocsSection = {
  label: "Getting Started",
  pages: [
    {
      slug: "introduction",
      title: "Introduction",
      description: "What the Yango platform is, in one mental model.",
      stub: false,
      blocks: [
        {
          kind: "paragraph",
          text: [
            "Yango is a ",
            { strong: "pluggable ERP/PIM platform" },
            ". A buyer web app talks ",
            { strong: "only" },
            " to a NestJS abstraction API (",
            { code: "apps/api" },
            "), which is the sole component that talks to ",
            { strong: "ERPNext" },
            " (the ERP) and ",
            { strong: "UnoPIM" },
            " (the catalog). Products are chosen by config, so a product can be swapped or disconnected without touching the web app.",
          ],
        },
        {
          kind: "callout",
          tone: "info",
          title: "The one-paragraph model",
          text: [
            "Web/mobile apps speak one dialect: REST against ",
            { code: "apps/api" },
            ", every response wrapped in the ",
            { code: "ApiResponse<T>" },
            " envelope. ",
            { code: "apps/api" },
            " owns all product dialects behind ports. Set a provider to ",
            { code: "none" },
            " and the platform still boots and serves an (empty) catalog.",
          ],
        },
        {
          kind: "heading",
          id: "why",
          text: "Why this shape",
        },
        {
          kind: "list",
          ordered: false,
          items: [
            [
              { strong: "Swappable products" },
              " — the ERP/PIM are dependencies behind ports, not the architecture.",
            ],
            [
              { strong: "One contract" },
              " — clients never learn a product URL or hold a product secret.",
            ],
            [
              { strong: "Honest disconnect" },
              " — the noop (",
              { code: "none" },
              ") adapter returns empty/disabled envelopes and never throws.",
            ],
          ],
        },
        {
          kind: "paragraph",
          text: [
            "Source of truth for the architecture: ",
            { code: "agent-os/specs/2026-06-17-repo-architecture/" },
            ".",
          ],
        },
      ],
    },
    {
      slug: "repositories",
      title: "Repositories & workspace",
      description: "The repos that make up the ecosystem and how they fit.",
      stub: false,
      blocks: [
        {
          kind: "paragraph",
          text: "The ecosystem is a multi-repo sibling workspace. Three repos sit next to each other under one parent directory:",
        },
        {
          kind: "table",
          headers: ["Repo", "What it is"],
          rows: [
            [
              { code: "yango-app" },
              "The platform spine: apps/api (abstraction API), buyer-web, the assessment app, mobile scaffolds, shared packages.",
            ],
            [
              { code: "yango-customizations" },
              "The additive ERPNext Frappe app, baked into the official ERPNext image via apps.json.",
            ],
            [
              { code: "yango-unopim" },
              "UnoPIM plus our additive customization package (packages/Yango/Customizations/).",
            ],
          ],
        },
        {
          kind: "paragraph",
          text: [
            "Siblings are pinned in ",
            { code: "workspace/workspace.repos.yaml" },
            " and reproduced with ",
            { code: "pnpm workspace:sync" },
            ". ERPNext itself is consumed as the official upstream image (an image-input pin), not a fork.",
          ],
        },
        {
          kind: "heading",
          id: "apps",
          text: "Apps & packages in yango-app",
        },
        {
          kind: "table",
          headers: ["Path", "Role"],
          rows: [
            [{ code: "apps/api" }, "NestJS 11 abstraction API — hexagonal ports/adapters."],
            [
              { code: "apps/buyer-web" },
              "Buyer SPA (Vite / React 19 / TanStack); talks only to apps/api.",
            ],
            [
              { code: "apps/assessment" },
              "Product-evaluation app on Supabase — and the home of these docs.",
            ],
            [
              { code: "packages/api-contract" },
              "Request/response DTOs shared by apps/api and the web apps.",
            ],
            [
              { code: "packages/shared-types" },
              "Cross-cutting types (the ApiResponse<T> envelope).",
            ],
            [
              { code: "packages/ui" },
              "Shared shadcn/ui component library (raw .tsx, no build step).",
            ],
          ],
        },
      ],
    },
    {
      slug: "local-development",
      title: "Local development",
      description: "Run the full product stack locally.",
      stub: false,
      blocks: [
        {
          kind: "paragraph",
          text: [
            "Bring up the unified local stack — ERPNext on ",
            { code: ":8090" },
            " and UnoPIM on ",
            { code: ":8096" },
            " — turnkey:",
          ],
        },
        {
          kind: "code",
          language: "bash",
          code: "pnpm infra:up      # docker compose up the unified stack\npnpm infra:down\npnpm infra:logs",
          caption: "Run the products before pnpm dev:api, and the API before pnpm dev:buyer-web.",
        },
        {
          kind: "heading",
          id: "live-edit",
          text: "Live-editing customizations",
        },
        {
          kind: "paragraph",
          text: [
            "Use the live-edit overlay to mount the ",
            { code: "yango-customizations" },
            " sibling so a ",
            { code: ".py" },
            " edit reloads without a rebuild:",
          ],
        },
        {
          kind: "code",
          language: "bash",
          code: "pnpm infra:up:erp-dev   # mounts the yango-customizations sibling",
          caption: "See infra/README.md for the full stack details.",
        },
        {
          kind: "heading",
          id: "dev-servers",
          text: "Dev servers",
        },
        {
          kind: "code",
          language: "bash",
          code: "pnpm dev:api          # NestJS abstraction API (port 4000)\npnpm dev:buyer-web    # buyer web app (Vite, port 5174)\npnpm dev:assessment   # assessment app (Vite)",
          caption: null,
        },
      ],
    },
  ],
};
